import { v4 as uuid } from 'uuid';

import { ApplicationRef, ComponentRef, createComponent, EmbeddedViewRef, Injectable, Injector, inject, DOCUMENT } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';

import { ApWindowService } from '@ap/cdk';
import { ApContentService } from '@ap/content';
import { ApCookieService } from '@ap/persistence';

import { ApCookieConsentComponent } from './cookie-consent.component';
import { ConsentLevels } from './cookie-consent.model';
import { CALLBACK_FNC, CONSENT_DATA, LAYOUT_DATA } from './cookie-consent.tokens';

/**
 * @ngdoc service
 * @name ApCookieConsentService
 */
@Injectable()
export class ApCookieConsentService {
    private readonly document = inject<Document>(DOCUMENT);
    private readonly cookieService = inject(ApCookieService);
    private readonly contentService = inject(ApContentService);
    private readonly appRef = inject(ApplicationRef);
    private readonly windowService = inject(ApWindowService);

    private readonly GDPR_COOKIE_NAME = 'CLC_GDPR';
    private readonly LEGACY_COOKIE_NAME = 'clc';

    private layout = '';

    private spacerDiv: HTMLElement;
    private readonly destroy$ = new Subject<void>();
    private componentRef: ComponentRef<ApCookieConsentComponent>;
    private isAttached = false;

    /**
     * Set type of layout. Can be set to `small` so that text has less width
     * @param layout
     */
    public setLayout(layout: string): void {
        this.layout = layout;
    }

    /**
     * Open the cookie consent overlay
     */
    public open(): void {
        if (!this.isAttached) {
            this.componentRef = createComponent(ApCookieConsentComponent, {
                elementInjector: this.createInjector(),
                environmentInjector: this.appRef.injector
            });
            const componentRootNode = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
            const setFixed = () => {
                this.spacerDiv.setAttribute('style', `padding-bottom: ${componentRootNode.getBoundingClientRect().height}px;`);
                componentRootNode.setAttribute('class', 'fixed');
            };
            this.spacerDiv = this.document.createElement('div');

            this.appRef.attachView(this.componentRef.hostView);
            this.isAttached = true;
            this.document.querySelector('body').prepend(componentRootNode, this.spacerDiv);

            fromEvent(this.windowService.getWindow(), 'scroll')
                .pipe(take(1), takeUntil(this.destroy$))
                .subscribe(() => setFixed());
            fromEvent(this.windowService.getWindow(), 'resize')
                .pipe(debounceTime(50), takeUntil(this.destroy$))
                .subscribe(() => setFixed());
        }
    }

    /**
     * Close the cookie consent
     * @param consentLevels
     */
    public close(consentLevels: ConsentLevels): void {
        this.setConsent(consentLevels);
        this.appRef.detachView(this.componentRef.hostView);
        this.destroy$.next();
        this.spacerDiv.remove();
        this.isAttached = false;
    }

    /**
     * This method reads the GDPR cookie and initialises a ConsentLevels object with the levels found in the cookie.
     * If cookie does not exist or is empty the ConsentLevels object defaults to all properties true.
     */
    public getConsent(): ConsentLevels {
        const consentLevels = new ConsentLevels();
        const gdprCookie = this.cookieService.get(this.GDPR_COOKIE_NAME);
        if (gdprCookie) {
            consentLevels.analytics = this.checkLevel('B', gdprCookie);
            consentLevels.advertisements = this.checkLevel('C', gdprCookie);
            consentLevels.personalisation = this.checkLevel('D', gdprCookie);
            consentLevels.empty = false;
        } else {
            consentLevels.analytics = true;
            consentLevels.advertisements = true;
            consentLevels.personalisation = true;
            consentLevels.empty = true;
        }
        return consentLevels;
    }

    /**
     * This method takes a ConsentLevels object and saves it in the GDPR cookie, functional (A) is always set,
     * the letters for analytics (B), advertisements (C) and personalisation (D) are set when the property is true.<br/>
     * The legacy cookie is also saved, only when analytics, advertisements and personalisation are true the level is set to 4,
     * otherwise the level is set to 1.<br/>
     * To both cookievalues a uuid is appended.
     */
    public setConsent(consentLevels: ConsentLevels): boolean {
        const gdprCookieData = this.getGdprCookieData(consentLevels);
        const legacyCookieData = this.getLegacyCookieData(consentLevels);

        const maxDate = new Date(new Date().setFullYear(new Date().getFullYear() + 1));
        const domainParts = this.windowService.location_hostname().split('.');

        if (domainParts.length > 2) {
            while (domainParts.length !== 2) {
                // Remove subdomains until only the root domain and TLD exist
                domainParts.shift();
            }
        }

        this.cookieService.set(this.LEGACY_COOKIE_NAME, legacyCookieData, maxDate.toUTCString(), domainParts.join('.'));
        return this.cookieService.set(this.GDPR_COOKIE_NAME, gdprCookieData, maxDate.toUTCString(), domainParts.join('.'));
    }

    private createInjector(): Injector {
        return Injector.create({
            providers: [
                { provide: CONSENT_DATA, useValue: this.getConsent() },
                { provide: LAYOUT_DATA, useValue: this.layout },
                { provide: CALLBACK_FNC, useValue: this.close.bind(this) },
                { provide: ApContentService, useValue: this.contentService }
            ]
        });
    }

    /**
     * @ngdoc private method
     * @name ApCookieConsentService#checkLevel
     * @methodOf ApCookieConsentService
     * @param [required]      The level the current consent should contain.
     * @param [consentStatus]   The current consent status.
     * @returns              It will return true if the required level is found in the current consent status.
     * @description                    This method will check if the current Consent status contains the required level.
     */
    private checkLevel(required: string, consentStatus: string): boolean {
        return consentStatus.indexOf(required.toUpperCase()) >= 0;
    }

    /**
     * @ngdoc private method
     * @name ApCookieConsentService#getLegacyCookieData
     * @methodOf ApCookieConsentService
     * @param [consentLevels]   ConsentLevels object
     * @returns   legacy cookie data
     * @description        The legacy level is calculated, in case all properties are true, 4 is returned, otherwise 1 is returned.<br/>
     * A uuid is appended to this value.
     */
    private getLegacyCookieData(consentLevels: ConsentLevels): string {
        return consentLevels.analytics && consentLevels.advertisements && consentLevels.personalisation ? `4:${uuid()}` : `1:${uuid()}`;
    }

    /**
     * @ngdoc private method
     * @name ApCookieConsentService#getGdprCookieData
     * @methodOf ApCookieConsentService
     * @param    [consentLevels] ConsentLevels object
     * @returns         The coded values.
     * @description               The properties in the ConsentLevels object are tested: functional (A) is always set,
     * the letters for analytics (B), advertisements (C) and personalisation (D) are set when the property is true.<br/>
     * A uuid is appended to this value.
     */
    private getGdprCookieData(consentLevels: ConsentLevels): string {
        let gdprCodes = 'A';
        if (consentLevels.analytics) {
            gdprCodes += 'B';
        }
        if (consentLevels.advertisements) {
            gdprCodes += 'C';
        }
        if (consentLevels.personalisation) {
            gdprCodes += 'D';
        }
        return `${gdprCodes}:${uuid()}`;
    }
}
